import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { useSignupDialog } from "@/contexts/SignupDialogContext";
import { useLoginDialog } from "@/contexts/LoginDialogContext";
import { useSiteSetting } from "@/hooks/useSiteSettings";

const SignupDialog = () => {
  const { isOpen, closeSignupDialog } = useSignupDialog();
  const { openLoginDialog } = useLoginDialog();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  

  const { data: registrationDisabled } = useSiteSetting("registration_disabled");
  const { data: googleEnabled } = useSiteSetting("auth_google_enabled");
  const { data: appleEnabled } = useSiteSetting("auth_apple_enabled");

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registrationDisabled) {
      toast.error("Registration is temporarily disabled. Please try again later.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Check your email to confirm your account!");
      closeSignupDialog();
      setEmail("");
      setPassword("");
      setFullName("");
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (error) toast.error("Google sign-in failed");
  };

  const handleAppleLogin = async () => {
    const { error } = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    if (error) toast.error("Apple sign-in failed");
  };

  const switchToLogin = () => {
    closeSignupDialog();
    openLoginDialog();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeSignupDialog()}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden rounded-2xl max-h-[95dvh] overflow-y-auto">
        <DialogTitle className="sr-only">Create Account</DialogTitle>
        <div className="p-4 md:p-6 pb-2 text-center">
          <span className="font-display text-xl md:text-2xl font-bold gradient-text">Gloogum</span>
          {registrationDisabled ? (
            <p className="text-destructive text-xs md:text-sm mt-1 font-medium">Registration is temporarily disabled. Please try again later.</p>
          ) : (
            <p className="text-muted-foreground text-xs md:text-sm mt-1">Create your account and join the community.</p>
          )}
        </div>

        <div className="px-4 md:px-6 pb-4 md:pb-6 pt-2">
          {(googleEnabled || appleEnabled) && (
          <div className="space-y-2.5 mb-4">
            {googleEnabled && (
            <Button variant="outline" className="w-full h-11 text-sm font-medium" onClick={handleGoogleLogin} disabled={registrationDisabled}>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>
            )}
            {appleEnabled && (
            <Button variant="outline" className="w-full h-11 text-sm font-medium" onClick={handleAppleLogin} disabled={registrationDisabled}>
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              Continue with Apple
            </Button>
            )}
          </div>
          )}

          {(googleEnabled || appleEnabled) && (
          <div className="flex items-center gap-3 mb-4">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground uppercase">or</span>
            <Separator className="flex-1" />
          </div>
          )}

          <form onSubmit={handleSignup} className="space-y-3">
            <div>
              <Label htmlFor="signup-name" className="text-xs">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="signup-name" type="text" placeholder="John Doe" value={fullName} onChange={e => setFullName(e.target.value)} className="pl-10 h-11" required />
              </div>
            </div>
            <div>
              <Label htmlFor="signup-email" className="text-xs">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="signup-email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 h-11" required />
              </div>
            </div>
            <div>
              <Label htmlFor="signup-password" className="text-xs">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="signup-password" type={showPassword ? "text" : "password"} placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 pr-10 h-11" required minLength={6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" variant="hero" className="w-full h-11" disabled={loading || registrationDisabled}>
              {registrationDisabled ? "Registration disabled" : loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-5">
            Already have an account?{" "}
            <button onClick={switchToLogin} className="text-primary font-medium hover:underline">Sign in</button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupDialog;
